import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isAdminRole, normalizeRole } from '../utils/rbacFrontend';

let inFlightPermissoesPromise = null;

const buildEmptyRecursoFlags = () => ({
  pode_visualizar: false,
  pode_criar: false,
  pode_editar: false,
  pode_excluir: false,
  pode_validar: false,
  pode_exportar: false,
  pode_importar: false,
  apenas_proprios: false,
  apenas_unidade: false,
});

const mergePermissoesListToRecursos = (permissoesList) => {
  const recursos = {};
  for (const item of permissoesList || []) {
    const recurso = (item?.recurso || '').toString().trim().toLowerCase();
    const acao = (item?.acao || '').toString().trim().toLowerCase();
    if (!recurso || !acao) {
      continue;
    }
    if (!recursos[recurso]) {
      recursos[recurso] = buildEmptyRecursoFlags();
    }
    if (acao === 'visualizar') recursos[recurso].pode_visualizar = true;
    if (acao === 'criar') recursos[recurso].pode_criar = true;
    if (acao === 'editar') recursos[recurso].pode_editar = true;
    if (acao === 'excluir') recursos[recurso].pode_excluir = true;
    if (acao === 'validar') recursos[recurso].pode_validar = true;
    if (acao === 'exportar') recursos[recurso].pode_exportar = true;
    if (acao === 'importar') recursos[recurso].pode_importar = true;
  }
  return recursos;
};

const buildPermissoesSnapshotFromUser = (user) => {
  if (!user) {
    return null;
  }

  const normalizedRole = normalizeRole(user?.role || user?.perfil);
  const isAdmin = isAdminRole(normalizedRole) || user?.is_admin === true;
  const recursos = mergePermissoesListToRecursos(user?.permissoes);

  return {
    perfil_id: user?.id_perfil || 0,
    perfil_nome: normalizedRole || 'USUARIO',
    is_admin: isAdmin,
    recursos,
  };
};

/**
 * Hook customizado para gerenciar permissões do usuário logado
 * 
 * Retorna:
 * - permissoes: objeto com todas as permissões do usuário
 * - loading: se está carregando as permissões
 * - error: erro ao carregar
 * - podeVisualizar(recurso): verifica se pode visualizar
 * - podeCriar(recurso): verifica se pode criar
 * - podeEditar(recurso): verifica se pode editar
 * - podeExcluir(recurso): verifica se pode excluir
 * - podeValidar(recurso): verifica se pode validar
 * - podeExportar(recurso): verifica se pode exportar
 * - temQualquerPermissao(recurso): verifica se tem alguma permissão
 * - recarregar(): recarrega as permissões
 */
export const usePermissoes = (options = {}) => {
  const { enabled = true } = options;
  const { user } = useAuth();
  const [permissoes, setPermissoes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const carregarPermissoes = useCallback(async () => {
    if (!enabled || !user) {
      setPermissoes(null);
      setError(null);
      setLoading(false);
      return;
    }

    const snapshotFromUser = buildPermissoesSnapshotFromUser(user);
    setPermissoes(snapshotFromUser);
    setError(null);

    if (user?.is_platform_admin === true) {
      // Platform admin routes are guarded by requirePlatformAdmin and do not depend on tenant RBAC grants.
      setLoading(false);
      return;
    }

    if (!user?.current_tenant_id) {
      // Tenant ausente/invalido: contexto seguro sem fail-open.
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      if (!inFlightPermissoesPromise) {
        inFlightPermissoesPromise = api.get('/api/permissoes/minhas').finally(() => {
          inFlightPermissoesPromise = null;
        });
      }

      const response = await inFlightPermissoesPromise;
      setPermissoes(response.data);
      setError(null);
    } catch (err) {
      // Mantem snapshot derivado de /auth/me quando /minhas falha.
      setError(err.message);
      if (!snapshotFromUser) {
        setPermissoes(null);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, user]);

  useEffect(() => {
    carregarPermissoes();
  }, [carregarPermissoes]);

  // Helper functions
  const obterPermissaoRecurso = (nomeRecurso) => {
    if (!permissoes || !permissoes.recursos) return null;
    return permissoes.recursos[nomeRecurso];
  };

  const podeVisualizar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_visualizar === true;
  };

  const podeCriar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_criar === true;
  };

  const podeEditar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_editar === true;
  };

  const podeExcluir = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_excluir === true;
  };

  const podeValidar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_validar === true;
  };

  const podeExportar = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.pode_exportar === true;
  };

  const temQualquerPermissao = (nomeRecurso) => {
    if (permissoes?.is_admin) return true;
    const perm = obterPermissaoRecurso(nomeRecurso);
    if (!perm) return false;
    
    return perm.pode_visualizar ||
           perm.pode_criar ||
           perm.pode_editar ||
           perm.pode_excluir ||
           perm.pode_validar ||
           perm.pode_exportar;
  };

  const apenasPropriosRegistros = (nomeRecurso) => {
    if (permissoes?.is_admin) return false;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.apenas_proprios === true;
  };

  const apenasUnidade = (nomeRecurso) => {
    if (permissoes?.is_admin) return false;
    const perm = obterPermissaoRecurso(nomeRecurso);
    return perm?.apenas_unidade === true;
  };

  return {
    permissoes,
    loading,
    error,
    isAdmin: permissoes?.is_admin || false,
    perfilNome: permissoes?.perfil_nome || '',
    podeVisualizar,
    podeCriar,
    podeEditar,
    podeExcluir,
    podeValidar,
    podeExportar,
    temQualquerPermissao,
    apenasPropriosRegistros,
    apenasUnidade,
    recarregar: carregarPermissoes
  };
};

/**
 * Hook simplificado para verificar permissão de um recurso específico
 * 
 * @param {string} nomeRecurso - Nome do recurso (ex: 'aulas_dadas', 'receitas')
 * @returns Objeto com todas as verificações de permissão para o recurso
 */
export const usePermissaoRecurso = (nomeRecurso) => {
  const {
    permissoes,
    loading,
    isAdmin,
    podeVisualizar,
    podeCriar,
    podeEditar,
    podeExcluir,
    podeValidar,
    podeExportar,
    temQualquerPermissao,
    apenasPropriosRegistros,
    apenasUnidade
  } = usePermissoes();

  return {
    loading,
    isAdmin,
    permissoes: permissoes?.recursos?.[nomeRecurso] || null,
    podeVisualizar: podeVisualizar(nomeRecurso),
    podeCriar: podeCriar(nomeRecurso),
    podeEditar: podeEditar(nomeRecurso),
    podeExcluir: podeExcluir(nomeRecurso),
    podeValidar: podeValidar(nomeRecurso),
    podeExportar: podeExportar(nomeRecurso),
    temAcesso: temQualquerPermissao(nomeRecurso),
    apenasPropriosRegistros: apenasPropriosRegistros(nomeRecurso),
    apenasUnidade: apenasUnidade(nomeRecurso)
  };
};

export default usePermissoes;
